import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
  Activity,
  AlertCircle,
} from 'lucide-react';
import {
  getAnalytics,
  getRevenueAnalytics,
  getOrdersAnalytics,
  getCustomersAnalytics,
  getDishesAnalytics,
  getSupplementsAnalytics,
  getRestaurantDashboardStats,
  handleRestaurantApiError,
  formatCurrency,
} from '../../../services/restaurantApi';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler);

const PRIMARY = '#054625';
const TERRA = '#EF4C23';
const OLIVE = '#72B744';

const NAMKE_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#051004',
      titleColor: '#FBF9D9',
      titleFont: { family: 'Archivo, sans-serif', weight: '600' },
      bodyColor: '#FBF9D9',
      bodyFont: { family: 'Archivo, sans-serif' },
      borderWidth: 0,
      padding: 12,
      cornerRadius: 12,
      displayColors: false,
    },
  },
  scales: {
    x: { ticks: { color: 'rgba(5,16,4,0.55)', font: { family: 'Archivo, sans-serif', size: 11 } }, grid: { display: false } },
    y: { beginAtZero: true, ticks: { color: 'rgba(5,16,4,0.55)', font: { family: 'Archivo, sans-serif', size: 11 } }, grid: { color: 'rgba(5,16,4,0.06)' } },
  },
};

function parsePeriodDays(period) {
  if (period === '7d') return 7;
  if (period === '90d') return 90;
  if (period === '1y') return 365;
  return 30;
}

function calcChange(series, key) {
  if (!Array.isArray(series) || series.length < 2) return null;
  const mid = Math.floor(series.length / 2);
  const first = series.slice(0, mid).reduce((acc, item) => acc + (item?.[key] || 0), 0);
  const second = series.slice(mid).reduce((acc, item) => acc + (item?.[key] || 0), 0);
  if (first <= 0 && second <= 0) return null;
  if (first <= 0) return null;
  return ((second - first) / first) * 100;
}

function shortDateLabel(dateValue) {
  const d = new Date(dateValue);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

const KpiCard = ({ label, value, trend, subtitle }) => (
  <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>{label}</span>
    <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(22px, 2.2vw, 28px)', letterSpacing: '-0.02em' }}>{value}</strong>
    {trend ? (
      <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, color: trend.direction === 'up' ? PRIMARY : TERRA }}>
        {trend.direction === 'up' ? '▲' : '▼'} {trend.label}
      </span>
    ) : (
      <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.6)' }}>{subtitle}</span>
    )}
  </div>
);

const ChartCard = ({ title, action, children }) => (
  <div style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
    <div className="flex items-center gap-3 flex-wrap">
      <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '20px', letterSpacing: '-0.02em' }}>{title}</h3>
      {action && <span style={{ marginLeft: 'auto' }}>{action}</span>}
    </div>
    {children}
  </div>
);

const NewRestaurantAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [supplementsMetric, setSupplementsMetric] = useState('revenue');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    general: {},
    revenue: { total: 0, change: null, chart: [] },
    orders: { total: 0, change: null, chart: [] },
    customers: { total: 0, change: null, chart: [] },
    dishes: { popular: [] },
    supplements: { summary: {}, trend: [], top: [] },
  });

  const loadAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        generalData,
        revenueData,
        ordersData,
        customersData,
        dishesData,
        supplementsData,
        statsData,
      ] = await Promise.all([
        getAnalytics({ period: selectedPeriod }),
        getRevenueAnalytics({ period: selectedPeriod }),
        getOrdersAnalytics({ period: selectedPeriod }),
        getCustomersAnalytics({ period: selectedPeriod }),
        getDishesAnalytics({ period: selectedPeriod }),
        getSupplementsAnalytics({ period: selectedPeriod }).catch(() => ({ summary: {}, trend: [], top: [] })),
        getRestaurantDashboardStats().catch(() => null),
      ]);

      const revenueArray = Array.isArray(revenueData) ? revenueData : [];
      const ordersArray = Array.isArray(ordersData) ? ordersData : [];
      const customersArray = Array.isArray(customersData) ? customersData : [];
      const dishesArray = Array.isArray(dishesData) ? dishesData : [];
      const supplementsTrend = Array.isArray(supplementsData?.trend) ? supplementsData.trend : [];
      const supplementsTop = Array.isArray(supplementsData?.top) ? supplementsData.top : [];

      setDashboardStats(statsData || null);
      const dashboardRating =
        statsData && typeof statsData.average_rating === 'number'
          ? statsData.average_rating
          : statsData?.average_rating != null
            ? Number(statsData.average_rating)
            : null;
      setAnalyticsData({
        general: {
          ...generalData,
          // L'endpoint /management/analytics ne fournit pas la note ; elle vient du dashboard.
          rating:
            (dashboardRating != null && !Number.isNaN(dashboardRating)
              ? dashboardRating
              : generalData?.average_rating ?? generalData?.rating) ?? 0,
          ratingChange: generalData?.ratingChange || 0,
        },
        revenue: {
          total: revenueArray.reduce((acc, item) => acc + (item?.revenue || 0), 0),
          change: calcChange(revenueArray, 'revenue'),
          chart: revenueArray,
        },
        orders: {
          total: ordersArray.reduce((acc, item) => acc + (item?.orders || 0), 0),
          change: calcChange(ordersArray, 'orders'),
          chart: ordersArray,
        },
        customers: {
          total: customersArray.reduce((acc, item) => acc + (item?.customers || 0), 0),
          change: calcChange(customersArray, 'customers'),
          chart: customersArray,
        },
        dishes: { popular: dishesArray },
        supplements: {
          summary: supplementsData?.summary || {},
          trend: supplementsTrend,
          top: supplementsTop,
        },
      });
    } catch (err) {
      console.error('Erreur lors du chargement des analyses:', err);
      setError(handleRestaurantApiError(err));
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  const revenueChartData = useMemo(() => ({
    labels: analyticsData.revenue.chart.map((item) => shortDateLabel(item.date)),
    datasets: [
      {
        label: 'Revenus (€)',
        data: analyticsData.revenue.chart.map((item) => item?.revenue || 0),
        borderColor: PRIMARY,
        backgroundColor: 'rgba(5, 70, 37, 0.08)',
        pointBackgroundColor: PRIMARY,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
        fill: true,
        tension: 0.35,
      },
    ],
  }), [analyticsData.revenue.chart]);

  const ordersChartData = useMemo(() => ({
    labels: analyticsData.orders.chart.map((item) => shortDateLabel(item.date)),
    datasets: [
      {
        label: 'Commandes',
        data: analyticsData.orders.chart.map((item) => item?.orders || 0),
        backgroundColor: TERRA,
        borderRadius: 6,
        maxBarThickness: 28,
      },
    ],
  }), [analyticsData.orders.chart]);

  const customersChartData = useMemo(() => ({
    labels: analyticsData.customers.chart.map((item) => shortDateLabel(item.date)),
    datasets: [
      {
        label: 'Clients',
        data: analyticsData.customers.chart.map((item) => item?.customers || 0),
        borderColor: OLIVE,
        backgroundColor: 'rgba(114, 183, 68, 0.12)',
        pointBackgroundColor: OLIVE,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
        fill: true,
        tension: 0.35,
      },
    ],
  }), [analyticsData.customers.chart]);

  const supplementsChartData = useMemo(() => {
    const label = supplementsMetric === 'units' ? 'Suppléments vendus' : 'CA suppléments (€)';
    return {
      labels: analyticsData.supplements.trend.map((item) => shortDateLabel(item.date)),
      datasets: [
        {
          label,
          data: analyticsData.supplements.trend.map((item) =>
            supplementsMetric === 'units' ? item?.units || 0 : item?.revenue || 0
          ),
          borderColor: PRIMARY,
          backgroundColor: supplementsMetric === 'units' ? TERRA : 'rgba(5, 70, 37, 0.08)',
          pointBackgroundColor: PRIMARY,
          pointRadius: 3,
          borderWidth: 2,
          borderRadius: supplementsMetric === 'units' ? 6 : undefined,
          maxBarThickness: 28,
          fill: supplementsMetric !== 'units',
          tension: 0.3,
        },
      ],
    };
  }, [analyticsData.supplements.trend, supplementsMetric]);

  const days = parsePeriodDays(selectedPeriod);
  const hasSupplementsData = analyticsData.supplements.trend.some((d) => (d?.units || 0) > 0);

  const reviewCountForRating =
    dashboardStats?.total_review_count ??
    dashboardStats?.restaurant_reviews_count ??
    0;
  const averageRatingDisplay =
    reviewCountForRating > 0
      ? Number(dashboardStats?.average_rating ?? analyticsData.general?.rating ?? 0)
      : null;

  const toTrend = (change) =>
    typeof change === 'number' && Number.isFinite(change) && Math.abs(change) > 0.05
      ? { direction: change >= 0 ? 'up' : 'down', label: `${change >= 0 ? '+' : ''}${change.toFixed(1)}%` }
      : null;

  if (loading) return <PageSkeleton />;

  const periodOptions = [
    { value: '7d', label: '7 jours' },
    { value: '30d', label: '30 jours' },
    { value: '90d', label: '90 jours' },
    { value: '1y', label: '1 an' },
  ];

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(18px, 2.2vw, 28px)' }}>
      {/* Header */}
      <div className="flex items-end gap-4 flex-wrap">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>
            Vue {days} jours
          </span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Analyses</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', background: '#FCFBF5', borderRadius: '999px', padding: '4px' }}>
          {periodOptions.map((opt) => {
            const active = selectedPeriod === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSelectedPeriod(opt.value)}
                style={{
                  border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13.5px',
                  padding: '9px 16px', borderRadius: '999px', whiteSpace: 'nowrap',
                  background: active ? PRIMARY : 'transparent', color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          <AlertCircle size={18} />
          {error?.message || error?.toString() || 'Une erreur est survenue'}
        </div>
      )}

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))', gap: 'clamp(12px, 1.6vw, 18px)' }}>
        <KpiCard
          label="Revenus"
          value={(analyticsData.revenue.total || 0) > 0 ? formatCurrency(analyticsData.revenue.total) : '—'}
          trend={toTrend(analyticsData.revenue.change)}
          subtitle="sur la période"
        />
        <KpiCard
          label="Commandes"
          value={analyticsData.orders.total || 0}
          trend={toTrend(analyticsData.orders.change)}
          subtitle="validées"
        />
        <KpiCard
          label="Clients uniques"
          value={(analyticsData.customers.total || 0) > 0 ? analyticsData.customers.total : '—'}
          trend={toTrend(analyticsData.customers.change)}
          subtitle="clients distincts"
        />
        <KpiCard
          label="CA suppléments"
          value={(analyticsData.supplements.summary?.total_revenue || 0) > 0 ? formatCurrency(analyticsData.supplements.summary.total_revenue) : '—'}
          subtitle={`${analyticsData.supplements.summary?.total_units || 0} vendus`}
        />
        <KpiCard
          label="Note moyenne"
          value={averageRatingDisplay != null && Number.isFinite(averageRatingDisplay) ? `${averageRatingDisplay.toFixed(1)}/5` : '—'}
          trend={toTrend(analyticsData.general?.ratingChange)}
          subtitle={`${reviewCountForRating} avis`}
        />
      </div>

      {/* Graphes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 'clamp(14px, 1.8vw, 22px)' }}>

        <ChartCard title="Évolution du chiffre d'affaires">
          <div style={{ height: '260px' }}>
            {analyticsData.revenue.chart.length > 0 ? (
              <Line data={revenueChartData} options={NAMKE_CHART_OPTIONS} />
            ) : (
              <div className="flex items-center justify-center h-full" style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucune donnée</div>
            )}
          </div>
        </ChartCard>

        <ChartCard title="Commandes par jour">
          <div style={{ height: '260px' }}>
            {analyticsData.orders.chart.length > 0 ? (
              <Bar data={ordersChartData} options={NAMKE_CHART_OPTIONS} />
            ) : (
              <div className="flex items-center justify-center h-full" style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucune donnée</div>
            )}
          </div>
        </ChartCard>

        <ChartCard title="Clients uniques">
          <div style={{ height: '260px' }}>
            {analyticsData.customers.chart.length > 0 ? (
              <Line data={customersChartData} options={NAMKE_CHART_OPTIONS} />
            ) : (
              <div className="flex items-center justify-center h-full" style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucune donnée</div>
            )}
          </div>
        </ChartCard>

        <ChartCard
          title="Ventes des suppléments"
          action={
            <span className="flex items-center gap-1.5" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.55)' }}>
              <Activity size={13} /> interactif
            </span>
          }
        >
          <div className="flex gap-1" style={{ background: '#FBF9D9', borderRadius: '999px', padding: '4px', width: 'fit-content' }}>
            {[{ v: 'revenue', l: 'CA suppléments' }, { v: 'units', l: 'Quantités' }].map((opt) => {
              const active = supplementsMetric === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setSupplementsMetric(opt.v)}
                  style={{
                    border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13px',
                    padding: '8px 16px', borderRadius: '999px', whiteSpace: 'nowrap',
                    background: active ? PRIMARY : 'transparent', color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
                  }}
                >
                  {opt.l}
                </button>
              );
            })}
          </div>
          <div style={{ height: '220px' }}>
            {hasSupplementsData ? (
              supplementsMetric === 'units' ? (
                <Bar data={supplementsChartData} options={NAMKE_CHART_OPTIONS} />
              ) : (
                <Line data={supplementsChartData} options={NAMKE_CHART_OPTIONS} />
              )
            ) : (
              <div className="flex items-center justify-center h-full" style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucune vente de supplément sur la période</div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: 'rgba(5,16,4,0.6)' }}>Suppléments vendus</span>
              <strong style={{ fontSize: '15px' }}>{analyticsData.supplements.summary?.total_units || 0}</strong>
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: 'rgba(5,16,4,0.6)' }}>CA suppléments</span>
              <strong style={{ fontSize: '15px' }}>{formatCurrency(analyticsData.supplements.summary?.total_revenue || 0)}</strong>
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: 'rgba(5,16,4,0.6)' }}>Commandes concernées</span>
              <strong style={{ fontSize: '15px' }}>{analyticsData.supplements.summary?.orders_with_supplements || 0}</strong>
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: 'rgba(5,16,4,0.6)' }}>Taux d'attache</span>
              <strong style={{ fontSize: '15px' }}>{Number(analyticsData.supplements.summary?.attach_rate || 0).toFixed(1)}%</strong>
            </span>
          </div>
        </ChartCard>

        <ChartCard title="Top plats">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {analyticsData.dishes.popular.slice(0, 6).map((dish, index) => (
              <div key={`${dish?.name || 'dish'}-${index}`} className="flex items-center justify-between" style={{ background: '#FBF9D9', borderRadius: '18px', padding: '12px 16px' }}>
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '15px' }}>{dish?.name || 'Plat'}</strong>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.6)' }}>{dish?.orders || 0} commandes</span>
                </span>
                <strong style={{ fontSize: '14.5px' }}>{formatCurrency(dish?.revenue || 0)}</strong>
              </div>
            ))}
            {analyticsData.dishes.popular.length === 0 && <p style={{ margin: 0, fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucun plat sur la période.</p>}
          </div>
        </ChartCard>

        <ChartCard title="Top suppléments">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {analyticsData.supplements.top.slice(0, 6).map((sup) => (
              <div key={sup.supplement_id} className="flex items-center justify-between" style={{ background: '#FBF9D9', borderRadius: '18px', padding: '12px 16px' }}>
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '15px' }}>{sup.name}</strong>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.6)' }}>{sup.dish_name} · {sup.units} vendus</span>
                </span>
                <strong style={{ fontSize: '14.5px' }}>{formatCurrency(sup.revenue || 0)}</strong>
              </div>
            ))}
            {analyticsData.supplements.top.length === 0 && <p style={{ margin: 0, fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucune donnée de supplément.</p>}
          </div>
        </ChartCard>

        <div style={{ gridColumn: '1 / -1', background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '20px', letterSpacing: '-0.02em' }}>Signaux clients</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px' }}>
            <span style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: 'rgba(5,16,4,0.6)' }}>Avis restaurant</span>
              <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px' }}>{dashboardStats?.total_review_count ?? dashboardStats?.restaurant_reviews_count ?? 0}</strong>
            </span>
            <span style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: 'rgba(5,16,4,0.6)' }}>Favoris restaurant</span>
              <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px' }}>{dashboardStats?.restaurant_favorites_count ?? 0}</strong>
            </span>
            <span style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: 'rgba(5,16,4,0.6)' }}>Favoris plats</span>
              <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px' }}>{dashboardStats?.dish_favorites_count ?? 0}</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewRestaurantAnalytics;
